import { useEffect, useState } from "react";
import { MapControl, useMap } from "@vis.gl/react-google-maps";
import { UndoRedoControl } from "../../services/maps/undo-redo-control";

interface DrawingExampleProps {
  onPolygonComplete: (polygon: {
    type: string;
    coordinates: number[][][];
  }) => void;
  initialPolygon?: {
    type: string;
    coordinates: number[][][];
  };
}

export default function DrawingExample({
  onPolygonComplete,
  initialPolygon,
}: DrawingExampleProps) {
  const map = useMap();
  const [drawingManager, setDrawingManager] =
    useState<google.maps.drawing.DrawingManager | null>(null);

  useEffect(() => {
    if (!map) return;

    const manager = new google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
    });

    manager.setMap(map);
    setDrawingManager(manager);

    // Draw initial polygon if it exists
    if (initialPolygon && initialPolygon.coordinates[0].length > 0) {
      const paths = initialPolygon.coordinates[0].map((coord) => ({
        lat: coord[1],
        lng: coord[0],
      }));

      const polygon = new google.maps.Polygon({
        paths,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: true,
      });

      polygon.setMap(map);

      // Fit bounds to show the polygon
      const bounds = new google.maps.LatLngBounds();
      paths.forEach((path) => bounds.extend(path));
      map.fitBounds(bounds);

      // Update coordinates when polygon is edited
      ["insert_at", "remove_at", "set_at"].forEach((event) => {
        google.maps.event.addListener(polygon.getPath(), event, () => {
          const coordinates = polygon
            .getPath()
            .getArray()
            .map((latLng) => [latLng.lng(), latLng.lat()]);
          onPolygonComplete({
            type: "Polygon",
            coordinates: [coordinates],
          });
        });
      });
    }

    return () => {
      manager.setMap(null);
    };
  }, [map, initialPolygon]);

  useEffect(() => {
    if (!drawingManager) return;

    const polygonCompleteListener = google.maps.event.addListener(
      drawingManager,
      "polygoncomplete",
      (polygon: google.maps.Polygon) => {
        // Make the polygon editable
        polygon.setEditable(true);

        // Convert polygon to GeoJSON coordinates
        const coordinates = polygon
          .getPath()
          .getArray()
          .map((latLng) => [latLng.lng(), latLng.lat()]);

        onPolygonComplete({
          type: "Polygon",
          coordinates: [coordinates],
        });

        // Update coordinates when polygon is edited
        ["insert_at", "remove_at", "set_at"].forEach((event) => {
          google.maps.event.addListener(polygon.getPath(), event, () => {
            const coordinates = polygon
              .getPath()
              .getArray()
              .map((latLng) => [latLng.lng(), latLng.lat()]);
            onPolygonComplete({
              type: "Polygon",
              coordinates: [coordinates],
            });
          });
        });

        // Switch back to hand tool after drawing
        drawingManager.setDrawingMode(null);
      }
    );

    return () => {
      google.maps.event.removeListener(polygonCompleteListener);
    };
  }, [drawingManager]);

  return (
    <MapControl position={google.maps.ControlPosition.TOP_CENTER}>
      <UndoRedoControl drawingManager={drawingManager} />
    </MapControl>
  );
}
